import React, { useState, useEffect, useContext } from "react";
import { useFirestore } from "../../firebase/FirestoreContext";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { EnvContext } from "../../../context/EnvContext";
import config from "../../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EditShippingOption = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const navigate = useNavigate();
  const { id } = useParams();

  const collectionPath =
    environment === "staging"
      ? config.enveironment.staging.collectionPath
      : config.enveironment.production.collectionPath;

  const [shippingData, setShippingData] = useState({
    name: "",
    slug: "",
    minimum_days: "",
    maximum_days: "",
    free_threshold: "",
    price: "",
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const shippingDocRef = doc(
      db,
      `${collectionPath}ShippingOptions/ShippingOptionsList/DataBase`,
      id
    );
    getDoc(shippingDocRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setShippingData({
            name: data.name || "",
            slug: data.slug || "",
            minimum_days: data.minimum_days || "",
            maximum_days: data.maximum_days || "",
            free_threshold: data.free_threshold || "",
            price: data.price || "",
          });
        } else {
          toast.error("Shipping option not found");
          navigate("/main/shipping-options");
        }
      })
      .catch((error) => {
        console.error("Error fetching shipping option:", error);
        toast.error("Error loading shipping option");
      });
  }, [db, id, collectionPath, navigate]);

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setShippingData({
      ...shippingData,
      name: newName,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    if (!shippingData.name || !shippingData.minimum_days || !shippingData.maximum_days || !shippingData.price) {
      toast.error("Please fill in all required fields");
      setLoading(false);
      return;
    }

    const shippingDocRef = doc(
      db,
      `${collectionPath}ShippingOptions/ShippingOptionsList/DataBase`,
      id
    );

    const updatedShippingOption = {
      name: shippingData.name,
      slug: shippingData.slug,
      minimum_days: parseInt(shippingData.minimum_days),
      maximum_days: parseInt(shippingData.maximum_days),
      free_threshold: parseFloat(shippingData.free_threshold) || 0,
      price: parseFloat(shippingData.price),
    };

    updateDoc(shippingDocRef, updatedShippingOption)
      .then(() => {
        toast.success("Shipping option updated successfully!");
        setTimeout(() => {
          navigate("/main/shipping-options");
        }, 1000);
      })
      .catch((error) => {
        console.error("Error updating shipping option:", error);
        toast.error("Error updating shipping option. Please try again.");
        setLoading(false);
      });
  };

  const handleDelete = () => {
    if (
      window.confirm(
        "Are you sure you want to delete this shipping option? This action cannot be undone."
      )
    ) {
      setDeleting(true);
      const shippingDocRef = doc(
        db,
        `${collectionPath}ShippingOptions/ShippingOptionsList/DataBase`,
        id
      );

      deleteDoc(shippingDocRef)
        .then(() => {
          toast.success("Shipping option deleted successfully!");
          setTimeout(() => {
            navigate("/main/shipping-options");
          }, 1000);
        })
        .catch((error) => {
          console.error("Error deleting shipping option:", error);
          toast.error("Error deleting shipping option. Please try again.");
          setDeleting(false);
        });
    }
  };

  const styles = {
    form: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: 20,
      border: "1px solid #ccc",
      borderRadius: 5,
      backgroundColor: "#f9f9f9",
      maxWidth: "600px",
    },
    formElement: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      marginBottom: 10,
    },
    formLabel: {
      marginBottom: 5,
      fontSize: 12,
      textTransform: "uppercase",
      color: "#4a4141",
      fontWeight: "bold",
    },
    formInput: {
      padding: "10px 15px",
      marginBottom: 10,
      fontSize: 16,
      border: ".5px solid #ccc",
      borderRadius: 5,
    },
    formInputDisabled: {
      padding: "10px 15px",
      marginBottom: 10,
      fontSize: 16,
      border: ".5px solid #ccc",
      borderRadius: 5,
      backgroundColor: "#e9ecef",
      color: "#6c757d",
    },
    buttonGroup: {
      display: "flex",
      gap: "10px",
      marginTop: "10px",
    },
    button: {
      padding: "12px 24px",
      backgroundColor: "#478169",
      color: "white",
      border: "none",
      borderRadius: 5,
      cursor: "pointer",
      fontSize: "16px",
      transition: "background-color 0.3s",
    },
    buttonDisabled: {
      padding: "12px 24px",
      backgroundColor: "#cccccc",
      color: "#666666",
      border: "none",
      borderRadius: 5,
      cursor: "not-allowed",
      fontSize: "16px",
    },
    deleteButton: {
      padding: "12px 24px",
      backgroundColor: "#dc3545",
      color: "white",
      border: "none",
      borderRadius: 5,
      cursor: "pointer",
      fontSize: "16px",
      transition: "background-color 0.3s",
    },
    helpText: {
      fontSize: 12,
      color: "#6c757d",
      marginTop: -8,
      marginBottom: 10,
    },
    backButton: {
      padding: "8px 16px",
      backgroundColor: "#f8f9fa",
      color: "#495057",
      border: "1px solid #dee2e6",
      borderRadius: 5,
      cursor: "pointer",
      fontSize: "14px",
      transition: "background-color 0.3s",
    },
  };

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      <div style={{ marginBottom: "20px" }}>
        <button
          type="button"
          onClick={() => navigate("/main/shipping-options")}
          style={styles.backButton}
        >
          ← Back to List
        </button>
      </div>
      <h2>Edit Shipping Option</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Name: *</label>
          <input
            type="text"
            value={shippingData.name}
            onChange={handleNameChange}
            style={styles.formInput}
            required
          />
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Slug:</label>
          <input
            type="text"
            value={shippingData.slug}
            style={styles.formInputDisabled}
            disabled
          />
          <div style={styles.helpText}>
            The slug cannot be changed after creation
          </div>
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Minimum Days: *</label>
          <input
            type="number"
            min="0"
            value={shippingData.minimum_days}
            onChange={(e) =>
              setShippingData({
                ...shippingData,
                minimum_days: e.target.value,
              })
            }
            style={styles.formInput}
            required
          />
          <div style={styles.helpText}>
            Minimum number of days for delivery
          </div>
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Maximum Days: *</label>
          <input
            type="number"
            min="0"
            value={shippingData.maximum_days}
            onChange={(e) =>
              setShippingData({
                ...shippingData,
                maximum_days: e.target.value,
              })
            }
            style={styles.formInput}
            required
          />
          <div style={styles.helpText}>
            Maximum number of days for delivery
          </div>
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Free Threshold:</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={shippingData.free_threshold}
            onChange={(e) =>
              setShippingData({
                ...shippingData,
                free_threshold: e.target.value,
              })
            }
            style={styles.formInput}
          />
          <div style={styles.helpText}>
            Minimum order amount for free shipping (leave 0 for no free
            shipping)
          </div>
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Price: *</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={shippingData.price}
            onChange={(e) =>
              setShippingData({ ...shippingData, price: e.target.value })
            }
            style={styles.formInput}
            required
          />
          <div style={styles.helpText}>Shipping price in dollars</div>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="submit"
            disabled={loading}
            style={loading ? styles.buttonDisabled : styles.button}
          >
            {loading ? "Updating..." : "Update Shipping Option"}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={deleting ? styles.buttonDisabled : styles.deleteButton}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditShippingOption;
